/**
 * Module that provides campaign definitions,
 * governance rules & message metadata from
 * targeted messaging service.
 */
import * as Configuration from "../Configuration/Configuration";
import { getDynamicSetting } from "../Configuration/DynamicConfiguration";
import { TULIPS_LOAD_TIMEOUT } from "./TargetedMessagingConstants";
import { TargetedMessagingClient, getDebugCampaignDefinitions, getDebugUserFacts } from "./TargetedMessagingClient";
import { createTimeoutPromise } from "../Utils";
export class AssetsProvider {
  constructor() {
    this.configuration = Configuration.get();
    // Enable TargetedMessaging client only if authCallback is provided
    // This is for cases where existing Floodgate hosts are not yet on-boarded into web surfaces flow
    const floodgateInitOptions = this.configuration && this.configuration.getFloodgateInitOptions();
    const commonInitOptions = this.configuration && this.configuration.getCommonInitOptions();
    const authTokenCallback = floodgateInitOptions && floodgateInitOptions.authTokenCallback;
    if (authTokenCallback && (authTokenCallback.getAuthToken || authTokenCallback.getUserPuid) || this.isAugLoopEnabled()) {
      const initOptions = this.configuration.getCommonInitOptions();
      this.tmsgClient = new TargetedMessagingClient(initOptions.appId.toString(), "Web" /*platform*/, initOptions.build, initOptions.originalLocale, this.extractCountryFromLocale(initOptions.originalLocale), this.configuration.getSdkSessionId(), floodgateInitOptions.authTokenCallback, floodgateInitOptions.augLoopCallback, floodgateInitOptions.campaignFlights, floodgateInitOptions.campaignQueryParameters, commonInitOptions.ageGroup, commonInitOptions.authenticationType);
    }
  }
  /**
   * Get campaign definitions from TMS
   */
  getCampaignDefinitions() {
    const debugResponse = getDebugCampaignDefinitions();
    if (debugResponse && debugResponse.content) {
      return Promise.resolve(debugResponse.content);
    }
    if (!this.tmsgClient) {
      return Promise.resolve(null);
    }
    return this.tmsgClient.getContent("CampaignContent" /* campaignContent */).then(_ref => {
      let {
        content
      } = _ref;
      return content;
    });
  }
  getCachedCampaignDefinitions() {
    var _a;
    return (_a = this.tmsgClient) === null || _a === void 0 ? void 0 : _a.getCachedContent("CampaignContent" /* campaignContent */);
  }
  getUserGovernanceRules() {
    if (!this.tmsgClient) {
      return Promise.resolve(null);
    }
    return this.tmsgClient.getContent("UserGovernance" /* userGovernanceRules */).then(_ref2 => {
      let {
        content
      } = _ref2;
      return content;
    });
  }
  getMessageMetadata() {
    if (!this.tmsgClient) {
      return Promise.resolve(null);
    }
    return this.tmsgClient.getContent("MessageMetadata" /* messageMetadata */).then(_ref3 => {
      let {
        content
      } = _ref3;
      return content;
    });
  }
  /**
   * Get TULIPS user facts from AugLoop or GWS endpoint
   */
  getUserFacts() {
    const debugResponse = getDebugUserFacts();
    if (debugResponse && debugResponse.content) {
      return Promise.resolve(debugResponse.content);
    }
    if (!this.tmsgClient) {
      return Promise.resolve(null);
    }
    // Try to get facts from AL first
    if (this.isAugLoopEnabled()) {
      const timeoutMilliseconds = getDynamicSetting("TulipsLoadTimeout" /* tulipsLoadTimeout */, TULIPS_LOAD_TIMEOUT);
      const timeoutPromise = createTimeoutPromise(timeoutMilliseconds, this.getAugLoopUserFacts());
      return timeoutPromise.then(userFacts => userFacts).catch(() => this.getSubstrateUserFacts());
    }
    return this.getSubstrateUserFacts();
  }
  getCachedUserFacts() {
    var _a;
    return (_a = this.tmsgClient) === null || _a === void 0 ? void 0 : _a.getCachedContent("UserFacts" /* userFacts */);
  }
  setCampaignAction(beaconParameters) {
    if (!this.tmsgClient) {
      return;
    }
    const initOptions = this.configuration.getCommonInitOptions();
    const floodgateInitOptions = this.configuration.getFloodgateInitOptions();
    return this.tmsgClient.sendBeaconRequestToService(initOptions.appId.toString(), beaconParameters, "Web" /*platform*/, initOptions.build, initOptions.originalLocale, this.extractCountryFromLocale(initOptions.originalLocale), this.configuration.getSdkSessionId(), floodgateInitOptions.authTokenCallback);
  }
  // Floodgate doesn't have country, so we have to use locale as a proxy and extract the country code
  extractCountryFromLocale(locale) {
    if (!locale) {
      return undefined;
    }
    const splitLocale = locale.split("-");
    if (splitLocale.length === 2) {
      return splitLocale[1];
    } else {
      return undefined;
    }
  }
  getSubstrateUserFacts() {
    if (!this.tmsgClient) {
      return Promise.resolve(null);
    }
    return this.tmsgClient.getUserFactsContent("UserFacts" /* userFacts */).then(_ref4 => {
      let {
        content
      } = _ref4;
      return content;
    });
  }
  getAugLoopUserFacts() {
    if (!this.tmsgClient) {
      return Promise.resolve(null);
    }
    return this.tmsgClient.getAugLoopUserFactsContent("UserFacts" /* userFacts */).then(_ref5 => {
      let {
        content
      } = _ref5;
      return content;
    });
  }
  isAugLoopEnabled() {
    var _a, _b, _c;
    return (_c = (_b = (_a = this.configuration) === null || _a === void 0 ? void 0 : _a.getFloodgateInitOptions()) === null || _b === void 0 ? void 0 : _b.augLoopCallback) === null || _c === void 0 ? void 0 : _c.isEnabled;
  }
}
let assetsProvider;
export function get() {
  if (!assetsProvider) {
    assetsProvider = new AssetsProvider();
  }
  return assetsProvider;
}